import axiosClient from "./axiosClient";

export class CatalogApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = "CatalogApiError";
    this.status = status;
    this.payload = payload;
  }
}

export function unwrapCatalogResponse(response) {
  const envelope = response?.data ?? {};
  const success = envelope?.success ?? envelope?.Success;
  if (success === false) {
    throw new CatalogApiError(
      envelope?.message ?? envelope?.Message ?? "Catalogue request failed.",
      response?.status,
      envelope,
    );
  }
  return envelope?.payload ?? envelope?.Payload ?? envelope?.data ?? envelope?.Data ?? envelope;
}

function toCatalogError(error) {
  if (error instanceof CatalogApiError) return error;
  const envelope = error?.response?.data ?? {};
  return new CatalogApiError(
    envelope?.message ??
      envelope?.Message ??
      "Catalogue request failed. Please try again.",
    error?.response?.status,
    envelope,
  );
}

function imageUploadForm({ file, altText = "", sortOrder = 0, isPrimary = false }) {
  const form = new FormData();
  form.append("file", file);
  form.append("altText", altText ?? "");
  form.append("sortOrder", String(sortOrder ?? 0));
  form.append("isPrimary", String(Boolean(isPrimary)));
  return form;
}

async function request(config) {
  try {
    return unwrapCatalogResponse(await axiosClient(config));
  } catch (error) {
    throw toCatalogError(error);
  }
}

const paging = ({ page = 1, pageSize = 20, activeOnly = false } = {}) => ({
  page,
  pageSize,
  activeOnly,
});

export async function collectCatalogPages(fetchPage, options = {}) {
  const pageSize = options.pageSize ?? 100;
  const firstPage = await fetchPage({ ...options, page: 1, pageSize });
  const collected = [...(firstPage?.items ?? [])];
  const totalCount = Number(firstPage?.totalCount ?? collected.length);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  for (let page = 2; page <= totalPages; page += 1) {
    const result = await fetchPage({ ...options, page, pageSize });
    collected.push(...(result?.items ?? []));
  }

  return collected;
}

export const catalogApi = {
  listCatalogs: params =>
    request({ method: "get", url: "/catalogs", params: paging(params) }),
  getCatalog: catalogId =>
    request({ method: "get", url: `/catalogs/${catalogId}` }),
  createCatalog: payload =>
    request({ method: "post", url: "/catalogs", data: payload }),
  updateCatalog: (catalogId, payload) =>
    request({ method: "put", url: `/catalogs/${catalogId}`, data: payload }),
  activateCatalog: (catalogId, expectedVersion) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/activate`,
      data: { expectedVersion },
    }),
  deactivateCatalog: (
    catalogId,
    expectedVersion,
    replacementDefaultCatalogId = null,
    expectedReplacementVersion = null,
  ) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/deactivate`,
      data: {
        expectedVersion,
        replacementDefaultCatalogId,
        expectedReplacementVersion,
      },
    }),
  setDefaultCatalog: (catalogId, expectedVersion) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/set-default`,
      data: { expectedVersion },
    }),

  listCategories: (catalogId, params) =>
    request({
      method: "get",
      url: `/catalogs/${catalogId}/categories`,
      params: paging(params),
    }),
  createCategory: (catalogId, payload) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/categories`,
      data: payload,
    }),
  updateCategory: (catalogId, categoryId, payload) =>
    request({
      method: "put",
      url: `/catalogs/${catalogId}/categories/${categoryId}`,
      data: payload,
    }),
  activateCategory: (catalogId, categoryId, expectedVersion) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/categories/${categoryId}/activate`,
      data: { expectedVersion },
    }),
  deactivateCategory: (catalogId, categoryId, expectedVersion) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/categories/${categoryId}/deactivate`,
      data: { expectedVersion },
    }),

  listItems: (catalogId, params) =>
    request({
      method: "get",
      url: `/catalogs/${catalogId}/items`,
      params: paging(params),
    }),
  getItem: (catalogId, itemId) =>
    request({ method: "get", url: `/catalogs/${catalogId}/items/${itemId}` }),
  createItem: (catalogId, payload) =>
    request({ method: "post", url: `/catalogs/${catalogId}/items`, data: payload }),
  updateItem: (catalogId, itemId, payload) =>
    request({
      method: "put",
      url: `/catalogs/${catalogId}/items/${itemId}`,
      data: payload,
    }),
  activateItem: (catalogId, itemId, expectedVersion) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/activate`,
      data: { expectedVersion },
    }),
  deactivateItem: (catalogId, itemId, expectedVersion) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/deactivate`,
      data: { expectedVersion },
    }),

  listVariants: (catalogId, itemId, params) =>
    request({
      method: "get",
      url: `/catalogs/${catalogId}/items/${itemId}/variants`,
      params: paging(params),
    }),
  getVariant: (catalogId, itemId, variantId) =>
    request({
      method: "get",
      url: `/catalogs/${catalogId}/items/${itemId}/variants/${variantId}`,
    }),
  createVariant: (catalogId, itemId, payload) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/variants`,
      data: payload,
    }),
  updateVariant: (catalogId, itemId, variantId, payload) =>
    request({
      method: "put",
      url: `/catalogs/${catalogId}/items/${itemId}/variants/${variantId}`,
      data: payload,
    }),
  activateVariant: (catalogId, itemId, variantId, expectedVersion) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/variants/${variantId}/activate`,
      data: { expectedVersion },
    }),
  deactivateVariant: (
    catalogId,
    itemId,
    variantId,
    expectedVersion,
    replacementDefaultVariantId = null,
  ) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/variants/${variantId}/deactivate`,
      data: { expectedVersion, replacementDefaultVariantId },
    }),
  setDefaultVariant: (catalogId, itemId, variantId, expectedVersion) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/variants/${variantId}/set-default`,
      data: { expectedVersion },
    }),
  getMetaSyncStatuses: (catalogId, itemId) =>
    request({
      method: "get",
      url: `/catalogs/${catalogId}/items/${itemId}/meta-sync`,
    }),
  syncVariantToMeta: (catalogId, itemId, variantId) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/meta-sync/${variantId}`,
    }),

  listItemAliases: (catalogId, itemId, params) =>
    request({
      method: "get",
      url: `/catalogs/${catalogId}/items/${itemId}/aliases`,
      params: paging(params),
    }),
  addItemAlias: (catalogId, itemId, alias) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/aliases`,
      data: { alias },
    }),
  removeItemAlias: (catalogId, itemId, aliasId) =>
    request({
      method: "delete",
      url: `/catalogs/${catalogId}/items/${itemId}/aliases/${aliasId}`,
    }),
  listVariantAliases: (catalogId, itemId, variantId, params) =>
    request({
      method: "get",
      url: `/catalogs/${catalogId}/items/${itemId}/variants/${variantId}/aliases`,
      params: paging(params),
    }),
  addVariantAlias: (catalogId, itemId, variantId, alias) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/variants/${variantId}/aliases`,
      data: { alias },
    }),
  removeVariantAlias: (catalogId, itemId, variantId, aliasId) =>
    request({
      method: "delete",
      url: `/catalogs/${catalogId}/items/${itemId}/variants/${variantId}/aliases/${aliasId}`,
    }),

  listItemImages: (catalogId, itemId, params) =>
    request({
      method: "get",
      url: `/catalogs/${catalogId}/items/${itemId}/images`,
      params: paging(params),
    }),
  addItemImage: (catalogId, itemId, payload) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/images`,
      data: payload,
    }),
  uploadItemImage: (catalogId, itemId, payload) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/images/upload`,
      data: imageUploadForm(payload),
    }),
  setPrimaryItemImage: (catalogId, itemId, imageId) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/images/${imageId}/set-primary`,
    }),
  removeItemImage: (catalogId, itemId, imageId) =>
    request({
      method: "delete",
      url: `/catalogs/${catalogId}/items/${itemId}/images/${imageId}`,
    }),
  listVariantImages: (catalogId, itemId, variantId, params) =>
    request({
      method: "get",
      url: `/catalogs/${catalogId}/items/${itemId}/variants/${variantId}/images`,
      params: paging(params),
    }),
  addVariantImage: (catalogId, itemId, variantId, payload) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/variants/${variantId}/images`,
      data: payload,
    }),
  uploadVariantImage: (catalogId, itemId, variantId, payload) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/variants/${variantId}/images/upload`,
      data: imageUploadForm(payload),
    }),
  setPrimaryVariantImage: (catalogId, itemId, variantId, imageId) =>
    request({
      method: "post",
      url: `/catalogs/${catalogId}/items/${itemId}/variants/${variantId}/images/${imageId}/set-primary`,
    }),
  removeVariantImage: (catalogId, itemId, variantId, imageId) =>
    request({
      method: "delete",
      url: `/catalogs/${catalogId}/items/${itemId}/variants/${variantId}/images/${imageId}`,
    }),
};

catalogApi.listAllCatalogs = options =>
  collectCatalogPages(params => catalogApi.listCatalogs(params), options);

catalogApi.listAllItems = (catalogId, options) =>
  collectCatalogPages(
    params => catalogApi.listItems(catalogId, params),
    options,
  );

catalogApi.listAllCategories = (catalogId, options) =>
  collectCatalogPages(
    params => catalogApi.listCategories(catalogId, params),
    options,
  );

catalogApi.listAllVariants = (catalogId, itemId, options) =>
  collectCatalogPages(
    params => catalogApi.listVariants(catalogId, itemId, params),
    options,
  );

catalogApi.listAllItemAliases = (catalogId, itemId) =>
  collectCatalogPages(params => catalogApi.listItemAliases(catalogId, itemId, params));

catalogApi.listAllVariantAliases = (catalogId, itemId, variantId) =>
  collectCatalogPages(params =>
    catalogApi.listVariantAliases(catalogId, itemId, variantId, params),
  );

catalogApi.listAllItemImages = (catalogId, itemId) =>
  collectCatalogPages(params => catalogApi.listItemImages(catalogId, itemId, params));

catalogApi.listAllVariantImages = (catalogId, itemId, variantId) =>
  collectCatalogPages(params =>
    catalogApi.listVariantImages(catalogId, itemId, variantId, params),
  );
